"use strict";
/**
 * Logger utility for controlling logging across the site
 * Usage:
 * import { logger } from '@orlandohealth/build-kit';
 *
 * // Basic logging with different levels
 * logger.debug('Debug message');
 * logger.info('Info message');
 * logger.warn('Warning message');
 * logger.error('Error message');
 *
 * // Logging with namespace/context
 * logger.info('User action completed', 'auth');
 *
 * // Logging with additional metadata
 * logger.info('API response received', 'api', { statusCode: 200 });
 *
 * // Logging errors with stack traces
 * logger.error('Operation failed', 'api', new Error('Something went wrong'));
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.logger = exports.LogLevel = void 0;
var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["DEBUG"] = 0] = "DEBUG";
    LogLevel[LogLevel["INFO"] = 1] = "INFO";
    LogLevel[LogLevel["WARN"] = 2] = "WARN";
    LogLevel[LogLevel["ERROR"] = 3] = "ERROR";
    LogLevel[LogLevel["NONE"] = 4] = "NONE";
})(LogLevel || (exports.LogLevel = LogLevel = {}));
const DEFAULT_CONFIG = {
    level: process.env.NEXT_PUBLIC_LOG_LEVEL
        ? LogLevel[process.env.NEXT_PUBLIC_LOG_LEVEL]
        : LogLevel.INFO,
    namespaces: (process.env.NEXT_PUBLIC_DEBUG_NAMESPACES || "")
        .split(",")
        .filter(Boolean),
};
let config = { ...DEFAULT_CONFIG };
const isNamespaceEnabled = (namespace) => {
    if (config.level === LogLevel.NONE)
        return false;
    if (!namespace)
        return true;
    return (config.namespaces.length === 0 || config.namespaces.includes(namespace));
};
const formatMessage = (level, message, namespace, metadata) => {
    const timestamp = new Date().toISOString();
    const namespaceStr = namespace ? `:${namespace}` : "";
    let metadataStr = "";
    if (metadata) {
        if (metadata instanceof Error) {
            metadataStr = `\n${metadata.stack || metadata.message}`;
        }
        else if (typeof metadata === "object" && metadata !== null) {
            metadataStr = ` ${JSON.stringify(metadata)}`;
        }
        else {
            metadataStr = ` ${String(metadata)}`;
        }
    }
    return `[${timestamp}] [${level}${namespaceStr}] ${message}${metadataStr}`;
};
exports.logger = {
    setConfig: (newConfig) => {
        config = { ...config, ...newConfig };
    },
    getConfig: () => ({ ...config }),
    debug: (message, namespace, metadata) => {
        if (config.level === LogLevel.DEBUG && isNamespaceEnabled(namespace)) {
            console.debug(formatMessage("DEBUG", message, namespace, metadata));
        }
    },
    info: (message, namespace, metadata) => {
        if (config.level <= LogLevel.INFO && isNamespaceEnabled(namespace)) {
            console.info(formatMessage("INFO", message, namespace, metadata));
        }
    },
    warn: (message, namespace, metadata) => {
        if (config.level <= LogLevel.WARN && isNamespaceEnabled(namespace)) {
            console.warn(formatMessage("WARN", message, namespace, metadata));
        }
    },
    error: (message, namespace, metadata) => {
        if (config.level <= LogLevel.ERROR && isNamespaceEnabled(namespace)) {
            console.error(formatMessage("ERROR", message, namespace, metadata));
        }
    },
    group: (name, callback, namespace) => {
        if (config.level === LogLevel.DEBUG && isNamespaceEnabled(namespace)) {
            console.group(formatMessage("DEBUG", name, namespace));
            callback();
            console.groupEnd();
        }
    },
    isEnabled: (level = LogLevel.DEBUG, namespace) => config.level <= level && isNamespaceEnabled(namespace),
};
